import axios from 'axios';
import { API_BASE_URL } from '../constants'; // Removed API_ENDPOINTS as it's not directly used here for caching logic

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

const CACHE_EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_KEY_PREFIX = 'generic_api_cache_'; // Generic cache key prefix

// Request interceptor for generic caching
apiClient.interceptors.request.use(config => {
  // Only cache GET requests
  if (config.method === 'get') {
    const cacheKey = `${CACHE_KEY_PREFIX}${config.url}`; // Use full URL including query params for unique key
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData); // 'data' here is the full API response object
      if (Date.now() - timestamp < CACHE_EXPIRATION_TIME) {
        // Correctly short-circuit the request by rejecting with an axios.Cancel
        // Pass the original config as the second argument to axios.Cancel
        const cancelError = new axios.Cancel('Request aborted by cache', config);
        cancelError.cachedData = data; // Attach the full API response object here
        return Promise.reject(cancelError);
      } else {
        localStorage.removeItem(cacheKey); // Remove expired cache
      }
    }
  }
  return config;
}, error => {
  return Promise.reject(error);
});

// Response interceptor for generic caching
apiClient.interceptors.response.use(response => {
  // Only cache successful GET requests
  if (response.config.method === 'get' && response.status === 200) {
    const cacheKey = `${CACHE_KEY_PREFIX}${response.config.url}`; // Use full URL including query params for unique key
    localStorage.setItem(cacheKey, JSON.stringify({ data: response.data, timestamp: Date.now() }));
  }
  return response;
}, error => {
  // Handle requests aborted by cache
  if (axios.isCancel(error)) {
    // error.config should now be available because it was passed to the Cancel constructor
    // Resolve with the cached data that was attached to the cancel object
    // Ensure the resolved object mimics an Axios response structure correctly
    return Promise.resolve({
      data: error.cachedData, // This is the full API response object (e.g., {success: true, data: {...}})
      status: 200,
      statusText: 'OK (Cached)',
      headers: { 'Content-Type': 'application/json' }, // Mimic common headers
      config: error.config,
      request: {} // Mimic request object
    });
  }
  return Promise.reject(error);
});

export default apiClient;